import { AgentStatus } from '@21k/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const STAFF_PAGE_SIZE = 25;

import {
  downloadStaffBulkTemplate,
  getApiErrorMessage,
  listUsers,
  updateAgentStatus,
  uploadStaffBulk,
} from '../../api/client';
import type { StaffBulkImportResult, UserProfile } from '../../api/types';
import AgentStatusBadge from '../../components/AgentStatusBadge';
import AgentStatusSelect from '../../components/AgentStatusSelect';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { useToast } from '../../contexts/ToastContext';
import { canEditStaff, canWriteStaff } from '../../utils/permissions';

export default function UsersPage() {
  const { user } = useAuth();
  const { socket } = useSocket();
  const { showToast } = useToast();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkResult, setBulkResult] = useState<StaffBulkImportResult | null>(null);

  const canManageAgents = user ? canEditStaff(user) : false;

  const updateAgentInList = useCallback(
    (agentId: string, patch: Partial<Pick<UserProfile, 'agentStatus' | 'statusUpdatedAt'>>) => {
      setUsers((prev) =>
        prev.map((u) => (u.id === agentId ? { ...u, ...patch } : u)),
      );
    },
    [],
  );

  const loadUsers = useCallback(async (nextPage: number, nextSearch: string) => {
    setLoading(true);
    setError('');
    try {
      const result = await listUsers({
        q: nextSearch.trim() || undefined,
        page: nextPage,
        pageSize: STAFF_PAGE_SIZE,
      });
      setUsers(result.items);
      setTotal(result.total);
      setPage(result.page);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextSearch = searchInput.trim();
      setSearch((current) => {
        if (current !== nextSearch) setPage(1);
        return nextSearch;
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    void loadUsers(page, search);
  }, [loadUsers, page, search]);

  useEffect(() => {
    if (!socket) return;

    const onStatusChanged = ({
      agentId,
      status,
    }: {
      agentId: string;
      status: AgentStatus;
    }) => {
      updateAgentInList(agentId, {
        agentStatus: status,
        statusUpdatedAt: new Date().toISOString(),
      });
    };

    socket.on('agent:status_changed', onStatusChanged);
    return () => {
      socket.off('agent:status_changed', onStatusChanged);
    };
  }, [socket, updateAgentInList]);

  function closeBulkModal() {
    if (bulkBusy) return;
    setBulkOpen(false);
    setBulkFile(null);
    setBulkError('');
    setBulkResult(null);
  }

  async function handleDownloadTemplate() {
    setBulkError('');
    try {
      await downloadStaffBulkTemplate();
    } catch (err) {
      setBulkError(getApiErrorMessage(err));
    }
  }

  async function handleBulkUpload() {
    if (!bulkFile) return;
    setBulkBusy(true);
    setBulkError('');
    try {
      const result = await uploadStaffBulk(bulkFile);
      setBulkResult(result);
      await loadUsers(page, search);
      showToast({
        title: 'Staff upload complete',
        label: `${result.created} created, ${result.skipped} skipped, ${result.errors} errors`,
      });
    } catch (err) {
      setBulkError(getApiErrorMessage(err));
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleAgentStatusChange(agentId: string, status: AgentStatus) {
    setUpdatingId(agentId);
    try {
      const updated = await updateAgentStatus(agentId, status);
      updateAgentInList(agentId, {
        agentStatus: updated.agentStatus as AgentStatus,
        statusUpdatedAt: updated.statusUpdatedAt,
      });
    } catch (err) {
      showToast({
        title: 'Failed to update agent status',
        label: getApiErrorMessage(err),
      });
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <>
      <div className="page-header">
        <h1>Staff Management</h1>
        {user && canWriteStaff(user) && (
          <div className="page-header-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setBulkOpen(true)}
            >
              Upload Excel
            </button>
            <Link to="/users/new" className="btn btn-primary">
              Add user
            </Link>
          </div>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="staff-toolbar">
          <input
            className="form-control staff-search"
            type="search"
            placeholder="Search by name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <p className="pagination-meta">
            {total === 0
              ? 'No staff found'
              : `Showing ${(page - 1) * STAFF_PAGE_SIZE + 1}–${Math.min(page * STAFF_PAGE_SIZE, total)} of ${total}`}
          </p>
        </div>
        {loading && users.length === 0 ? (
          <div className="loading">Loading users…</div>
        ) : users.length === 0 ? (
          <p className="staff-empty">No staff match this search.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Account</th>
                  <th>Availability</th>
                  <th>System roles</th>
                  <th>Departments</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <Link to={`/users/${u.id}`}>
                        {u.firstName} {u.lastName}
                      </Link>
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <Badge label={u.role} />
                    </td>
                    <td>{u.isActive ? 'Active' : 'Inactive'}</td>
                    <td>
                      <div className="agent-status-row">
                        <AgentStatusBadge status={u.agentStatus} />
                        {canManageAgents && (
                          <AgentStatusSelect
                            value={u.agentStatus}
                            disabled={updatingId === u.id}
                            onChange={(status) => void handleAgentStatusChange(u.id, status)}
                          />
                        )}
                      </div>
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>
                      {u.systemRoles.length > 0
                        ? u.systemRoles.map((sr) => sr.role.name).join(', ')
                        : u.role === 'SUPER_ADMIN'
                          ? 'All modules'
                          : '—'}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>
                      {u.departmentMemberships.length === 0
                        ? '—'
                        : u.departmentMemberships
                            .map((m) => `${m.department.name} (${m.role})`)
                            .join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > STAFF_PAGE_SIZE && (
          <div className="pagination staff-pagination">
            <button
              type="button"
              className="pagination-page"
              disabled={page <= 1 || loading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </button>
            <span className="pagination-page active">{page}</span>
            <button
              type="button"
              className="pagination-page"
              disabled={page * STAFF_PAGE_SIZE >= total || loading}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {bulkOpen && (
        <div className="modal-backdrop" role="presentation" onClick={closeBulkModal}>
          <div
            className="modal-panel staff-bulk-modal"
            role="dialog"
            aria-labelledby="staff-bulk-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="staff-bulk-title">Upload staff Excel</h2>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={closeBulkModal}
                disabled={bulkBusy}
              >
                Close
              </button>
            </div>
            <div className="modal-body">
              <p className="staff-bulk-copy">
                Use columns <strong>First Name</strong>, <strong>Last Name</strong>, and{' '}
                <strong>Email</strong>. New staff are created as standard users with the{' '}
                <strong>Standard User</strong> system role.
              </p>
              <div className="staff-bulk-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => void handleDownloadTemplate()}
                  disabled={bulkBusy}
                >
                  Download template
                </button>
                <input
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={(e) => {
                    setBulkFile(e.target.files?.[0] ?? null);
                    setBulkResult(null);
                    setBulkError('');
                  }}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={!bulkFile || bulkBusy}
                  onClick={() => void handleBulkUpload()}
                >
                  {bulkBusy ? 'Uploading…' : 'Upload'}
                </button>
              </div>
              {bulkFile && (
                <p className="staff-bulk-file">Selected: {bulkFile.name}</p>
              )}
              {bulkError && <div className="alert alert-error">{bulkError}</div>}
              {bulkResult && (
                <div className="staff-bulk-result">
                  <p>
                    Created {bulkResult.created}, skipped {bulkResult.skipped}, errors{' '}
                    {bulkResult.errors}. Assigned role: {bulkResult.assignedSystemRole}.
                  </p>
                  {bulkResult.rows.length > 0 && (
                    <div className="table-wrap">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Status</th>
                            <th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkResult.rows.map((row) => (
                            <tr key={`${row.row}-${row.email}`}>
                              <td>{row.row}</td>
                              <td>
                                {row.firstName} {row.lastName}
                              </td>
                              <td>{row.email}</td>
                              <td>
                                <Badge
                                  label={row.status}
                                  className={
                                    row.status === 'created'
                                      ? 'badge-success'
                                      : row.status === 'skipped'
                                        ? 'badge-warning'
                                        : 'badge-danger'
                                  }
                                />
                              </td>
                              <td>{row.message ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
